import React, { useEffect, useRef } from "react";
import Hls from "hls.js";

type Props = {
  hlsUrl: string;
  isHover: boolean;
};

const HoverVideo: React.FC<Props> = ({ hlsUrl, isHover }) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const readyRef = useRef(false);
  const pauseTimer = useRef<number | null>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    readyRef.current = false;

    if (hlsRef.current) {
      try {
        hlsRef.current.destroy();
      } catch {}
      hlsRef.current = null;
    }

    const markReadyAndMaybePlay = () => {
      readyRef.current = true;

      if (isHover) {
        el.muted = true;
        el.playsInline = true;
        el.loop = true;
        el.play().catch(() => {});
      }
    };

    if (Hls.isSupported()) {
      const hls = new Hls();
      hlsRef.current = hls;

      hls.on(Hls.Events.MANIFEST_PARSED, markReadyAndMaybePlay);
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (data?.fatal) {
          try {
            hls.destroy();
          } catch {}
        }
      });

      hls.loadSource(hlsUrl);
      hls.attachMedia(el);
    } else if (el.canPlayType("application/vnd.apple.mpegurl")) {
      el.src = hlsUrl;
      el.addEventListener("canplay", markReadyAndMaybePlay, { once: true });

      return () => {
        el.removeEventListener("canplay", markReadyAndMaybePlay);
      };
    }

    return () => {
      try {
        hlsRef.current?.destroy();
      } catch {}
      hlsRef.current = null;
    };
  }, [hlsUrl, isHover]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    if (pauseTimer.current) {
      window.clearTimeout(pauseTimer.current);
      pauseTimer.current = null;
    }

    if (isHover) {
      if (readyRef.current) {
        el.play().catch(() => {});
      }
    } else {
      pauseTimer.current = window.setTimeout(() => {
        try {
          el.pause();
        } catch {}
      }, 120);
    }
  }, [isHover]);

  return (
    <video
      ref={videoRef}
      muted
      loop
      playsInline
      preload="metadata"
      style={{
        opacity: isHover ? 1 : 0,
        transition: "opacity 120ms linear",
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
        pointerEvents: "none",
        display: "block",
      }}
    />
  );
};

export default HoverVideo;